import React, { useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { GET_EMPLOYEES, GET_EMPLOYEE_UNSETTLED_ORDERS } from "./graphql";
import { formatDate, inr } from "./format";

/**
 * Pick a professional and the unsettled cash bookings a deposit covers.
 * Used both when generating a cash-deposit link and when linking an
 * unlinked Razorpay payment as a deposit.
 */
const EmployeeDepositPicker = ({
  employeeId,
  setEmployeeId,
  selectedOrderIds,
  setSelectedOrderIds,
  targetAmount,
}) => {
  const { data: employeesData } = useQuery(GET_EMPLOYEES, {
    variables: { data: {} },
  });
  const employees = useMemo(
    () =>
      [...(employeesData?.getEmployeelist?.employee || [])].sort((a, b) =>
        (a.fullName || "").localeCompare(b.fullName || ""),
      ),
    [employeesData],
  );

  const { data: ordersData, loading: ordersLoading } = useQuery(
    GET_EMPLOYEE_UNSETTLED_ORDERS,
    {
      variables: { employeeId },
      skip: !employeeId,
      fetchPolicy: "network-only",
    },
  );
  const orders = ordersData?.getEmployeeUnsettledOrders?.orders || [];

  // Fresh employee -> fresh selection
  useEffect(() => {
    setSelectedOrderIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const selectedTotal = orders
    .filter((o) => selectedOrderIds.includes(o.id))
    .reduce((sum, o) => sum + (o.settlementAmount || 0), 0);

  const toggle = (id) =>
    setSelectedOrderIds(
      selectedOrderIds.includes(id)
        ? selectedOrderIds.filter((x) => x !== id)
        : [...selectedOrderIds, id],
    );

  const matches =
    targetAmount == null || Math.abs(selectedTotal - targetAmount) <= 1;

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">
        Professional
        <select
          value={employeeId || ""}
          onChange={(e) => setEmployeeId(e.target.value || null)}
          className="mt-1 w-full border rounded-md px-3 py-2"
        >
          <option value="">Select professional</option>
          {employees.map((e) => (
            <option key={e.id} value={e.id}>
              {e.fullName} ({e.contactNumber})
            </option>
          ))}
        </select>
      </label>

      {employeeId && (
        <div className="border rounded-md">
          <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b text-sm">
            <span className="font-medium">Unsettled cash bookings</span>
            {orders.length > 0 && (
              <button
                type="button"
                className="text-blue-600"
                onClick={() =>
                  setSelectedOrderIds(
                    selectedOrderIds.length === orders.length
                      ? []
                      : orders.map((o) => o.id),
                  )
                }
              >
                {selectedOrderIds.length === orders.length
                  ? "Clear all"
                  : "Select all"}
              </button>
            )}
          </div>
          <div className="max-h-56 overflow-y-auto">
            {ordersLoading && (
              <p className="p-3 text-sm text-gray-500">Loading…</p>
            )}
            {!ordersLoading && orders.length === 0 && (
              <p className="p-3 text-sm text-gray-500">
                No unsettled cash for this professional.
              </p>
            )}
            {orders.map((o) => (
              <label
                key={o.id}
                className="flex items-center gap-3 px-3 py-2 text-sm border-b last:border-b-0 cursor-pointer hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={selectedOrderIds.includes(o.id)}
                  onChange={() => toggle(o.id)}
                />
                <span className="font-mono">{o.bookingId}</span>
                <span className="text-gray-500">{formatDate(o.bookingDate)}</span>
                <span className="text-gray-500">{o.paymentType}</span>
                <span className="ml-auto font-medium">
                  {inr(o.settlementAmount)}
                </span>
              </label>
            ))}
          </div>
          <div
            className={`px-3 py-2 text-sm border-t flex justify-between ${
              matches ? "bg-gray-50" : "bg-red-50 text-red-700"
            }`}
          >
            <span>Selected: {selectedOrderIds.length} booking(s)</span>
            <span className="font-semibold">
              {inr(selectedTotal)}
              {targetAmount != null && ` / payment ${inr(targetAmount)}`}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeDepositPicker;
